import { useRef } from "react";
import { RefreshCw, Play, Maximize } from "lucide-react";
import { captureEvent } from "@/lib/analyticsQueue";
import { Overlay } from "@/components/flowly/infra/Overlay/Overlay";
import { LightboxCloseButton } from "../shared/LightboxCloseButton";
import { LightboxSidebar } from "../shared/LightboxSidebar";
import { LightboxActionButton } from "../shared/LightboxActionButton";
import { LightboxShareLikeRow } from "../shared/LightboxShareLikeRow";
import { LightboxDetailsSection } from "../shared/LightboxDetailsSection";
import { LightboxCollapsibleSection } from "../shared/LightboxCollapsibleSection";
import { LightboxDeleteButton } from "../shared/LightboxDeleteButton";
import { LightboxNavigation } from "../shared/LightboxNavigation";
import { PIPELINE_TYPES } from "../shared/constants";
import { useSwipeNavigation } from "../shared/useSwipeNavigation";
import { ANALYTICS_EVENTS, PIPELINE_TO_FLOW } from "@shared/schemas/analytics";
import { useVideoLightbox } from "./useVideoLightbox";
import { useVideoPlayback } from "./useVideoPlayback";
import type { VideoLightboxProps } from "./types";
import { requestFullscreenOn } from "@/lib/fullscreen";

export function VideoLightbox({
  videoUrl,
  sourceImageUrl,
  prompt,
  createdAt,
  model,
  resolution,
  aspectRatio,
  pipeline,
  duration,
  onClose,
  onRegenerate,
  onDownload,
  onShare,
  onDelete,
  navigation,
}: VideoLightboxProps) {
  const swipeHandlers = useSwipeNavigation({
    onNext: navigation?.onNext,
    onPrev: navigation?.onPrev,
    enabled: !!navigation,
  });

  const { t, liked, formattedDate, modelDisplayName, handleToggleLike } = useVideoLightbox({
    videoUrl,
    sourceImageUrl,
    prompt,
    createdAt,
    model,
    navigation,
  });

  const handleTrackedDownload = () => {
    if (!onDownload) return;
    captureEvent(ANALYTICS_EVENTS.CONTENT_DOWNLOAD, { type: "video", flow: PIPELINE_TO_FLOW[pipeline ?? ""] ?? pipeline });
    onDownload();
  };

  const handleTrackedShare = () => {
    if (!onShare) return;
    captureEvent(ANALYTICS_EVENTS.CONTENT_SHARE, { type: "video", flow: PIPELINE_TO_FLOW[pipeline ?? ""] ?? pipeline });
    onShare();
  };

  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const { videoRef, isPlaying, togglePlayPause, onPlay, onPause, onEnded } = useVideoPlayback();

  const handleFullscreen = () => {
    // Cross-browser + no-op on iOS Safari (kills FRONTEND-T).
    requestFullscreenOn(mediaContainerRef.current);
  };

  const getPipelineDisplay = (type: string | undefined) => {
    if (!type) return undefined;
    if (type === PIPELINE_TYPES.TEXT_TO_VIDEO) return t.lightbox.pipelineTextToVideo;
    if (type === PIPELINE_TYPES.FRAMES_TO_VIDEO) return t.lightbox.pipelineFramesToVideo;
    return type;
  };

  return (
    <Overlay isOpen onClose={onClose} className="bg-black/90 p-0 cursor-pointer animate-fade-in" zIndex="z-[100]">
      <div
        className="flex flex-col md:flex-row gap-4 md:gap-6 w-full mx-4 md:mx-6 animate-scale-in relative cursor-default max-h-[90vh] overflow-y-auto md:overflow-visible md:h-[calc(100vh-80px)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Video */}
        <div className="flex-shrink-0 md:flex-1 flex items-center justify-center order-1 md:order-2 relative" {...swipeHandlers}>
          {/* Navigation Arrows */}
          {navigation && (
            <LightboxNavigation
              currentIndex={navigation.currentIndex}
              totalItems={navigation.totalItems}
              canGoNext={navigation.canGoNext}
              canGoPrev={navigation.canGoPrev}
              onNext={navigation.onNext}
              onPrev={navigation.onPrev}
            />
          )}

          <div
            ref={mediaContainerRef}
            className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-white/10 max-h-[72vh] md:max-h-[calc(100vh-120px)] cursor-pointer min-h-[200px] min-w-[150px] bg-black"
            onClick={togglePlayPause}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              // User opened this video deliberately → sound is expected.
              // Opt out of the global mute guard (see index.html).
              data-allow-sound="1"
              className="w-full h-full max-w-full max-h-[72vh] md:max-h-[calc(100vh-120px)] object-contain"
              loop
              playsInline
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
            />
            {/* Play/Pause button overlay - show when paused or on hover */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors active:scale-95">
                  <Play
                    className="w-8 h-8 md:w-10 md:h-10 text-white ml-1"
                    fill="white"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Options Sidebar */}
        <LightboxSidebar title={t.lightbox.options}>
          <LightboxCloseButton
            onClose={onClose}
            backLabel={t.lightbox.backToStudio}
          />

          {/* Primary actions — always visible */}
          <LightboxShareLikeRow
            onDownload={handleTrackedDownload}
            downloadLabel={t.lightbox.download}
            onShare={handleTrackedShare}
            shareLabel={t.lightbox.share}
            liked={liked}
            onToggleLike={handleToggleLike}
          />

          {/* Advanced — open on desktop, collapsed on mobile */}
          <LightboxCollapsibleSection title={t.lightbox.advancedSettings} defaultOpenDesktop>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-3">
              {onRegenerate && (
                <LightboxActionButton
                  icon={<RefreshCw className="w-4 h-4 text-brand" />}
                  label={t.lightbox.regenerate}
                  onClick={onRegenerate}
                  colorClass="bg-brand/10 hover:bg-brand/20"
                  iconColorClass="bg-brand/20"
                />
              )}
              <LightboxActionButton
                icon={<Maximize className="w-4 h-4 text-sky-400" />}
                label={t.lightbox.fullscreen}
                onClick={handleFullscreen}
                colorClass="bg-sky-500/10 hover:bg-sky-500/20"
                iconColorClass="bg-sky-500/20"
              />
            </div>
          </LightboxCollapsibleSection>

          {/* Delete Button */}
          {onDelete && <LightboxDeleteButton onDelete={onDelete} />}

          {/* Details — collapsed by default */}
          <LightboxCollapsibleSection title={t.lightbox.videoDetails}>
            <LightboxDetailsSection
              formattedDate={formattedDate}
              dateLabel={t.lightbox.creationDate}
              model={modelDisplayName}
              modelLabel={modelDisplayName ? t.lightbox.model : undefined}
              sourceImageUrl={sourceImageUrl}
              sourceImageLabel={t.lightbox.sourceImage}
              resolution={resolution}
              resolutionLabel={resolution ? t.lightbox.resolution : undefined}
              aspectRatio={aspectRatio}
              aspectRatioLabel={aspectRatio ? t.lightbox.aspectRatio : undefined}
              duration={duration}
              durationLabel={duration != null ? t.lightbox.duration : undefined}
              pipeline={getPipelineDisplay(pipeline)}
              pipelineLabel={pipeline ? t.lightbox.pipeline : undefined}
              prompt={prompt}
              promptLabel={prompt ? t.lightbox.prompt : undefined}
            />
          </LightboxCollapsibleSection>
        </LightboxSidebar>
      </div>
    </Overlay>
  );
}
